import { useEffect, useRef } from 'react';
import TagManager from 'react-gtm-module';
import { useStepsContext } from '../../Hooks/useStepsContext';
import { isEmailValid } from '../../Utils/functions';
import { laneCopy } from '../../Utils/laneCopy';
import { resolveIncomeLane, LANE_BORDERLINE } from '../../Utils/incomeLane';
import AlertComponent from '../Alert/AlertComponent';
import Input from '../Input/Input';
import Button from '../Button/Button';
import BotResponse from '../BotResponse/BotResponse';
import classes from 'classnames';

/**
 * Step 15 — the destination for visitors sorted below the income floor.
 *
 * Follows the 12_SubscribeToNews pattern deliberately, not the 15_ReadyToSpeak one: that component
 * needs a useEffect on [email] to work around next() reading the closure's state, and it
 * auto-advances on remount, which is what makes step 12 non-editable. Neither is reproduced here.
 *
 * Every sentence comes from laneCopy; this file contributes no user-facing wording of its own.
 *
 * @author Cristian David
 * @since 2026-09-11
 */
export default function FreeDatabaseOptin() {
  const { state, setState, next, back, setShouldSave, error, setError, animate, makeFakeLoading, fakeLoadingDuration, stepPassed } =
    useStepsContext();

  const { name, email, gender, income } = state;
  const copy = laneCopy.belowFloor;
  const isTestLead = name.toLowerCase().includes('test');
  const lane = resolveIncomeLane(gender, income);

  // Which opening to show depends on how the visitor arrived, and the lane is the robust signal
  // for that rather than the disclaimer field. Only two lanes can reach this screen: below_floor
  // (sorted here from the income step, never saw the price) and borderline (could only arrive by
  // declining the price on screen 9). Keying on the lane cannot go stale: the disclaimer field
  // persists and is not cleared when income changes, so a visitor who declined at a higher income
  // and then lowered it below the floor would otherwise keep the wrong opening.
  const cameFromPriceScreen = lane === LANE_BORDERLINE;
  const opening = cameFromPriceScreen
    ? copy.openingFromPrice
    : copy.openingFromIncome.replace('{name}', name);

  // View event, kept to one push by the ref (StrictMode double-invokes effects in dev, and any
  // real remount would double it in production). Carries the lane and the copy id, matching the
  // price-screen view event so one GTM variable serves both lane screens (spec IQ-9).
  const viewFired = useRef(false);
  useEffect(() => {
    if (stepPassed || isTestLead || viewFired.current) return;
    viewFired.current = true;

    TagManager.dataLayer({
      dataLayer: {
        event: 'allybot2_free_db_optin_page',
        allybot2_income_lane: lane,
        allybot2_disclosure_copy_id: copy.copyId,
      },
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleEmailChange = (email) => {
    setShouldSave(true);
    setState((state) => ({ ...state, email }));
  };

  const handleNext = (e) => {
    e.preventDefault();
    makeFakeLoading();

    setTimeout(() => {
      if (!email) return setError('empty');
      if (!isEmailValid(email)) return setError('invalid');

      // Pushed here rather than next to the redirect in next(): redirect() navigates 10ms after
      // clearing the token, which does not reliably leave the tag time to send its beacon.
      if (!name.toLowerCase().includes('test')) {
        TagManager.dataLayer({
          dataLayer: {
            event: 'allybot2_free_db_submitted',
            allybot2_income_lane: lane,
            allybot2_disclosure_copy_id: copy.copyId,
          },
        });
      }

      next();
    }, fakeLoadingDuration);
  };

  return (
    <form name="freeDatabase" onSubmit={handleNext}>
      {error && <AlertComponent text="There was a problem with your submission. Errors are marked below." />}

      <BotResponse className={classes(animate(0), 'mb-20px')}>{opening}</BotResponse>

      {copy.body.map((line, i) => (
        <BotResponse key={line} className={classes(animate(i + 1), 'mb-20px')}>
          {line}
        </BotResponse>
      ))}

      <BotResponse className={classes(animate(copy.body.length + 1), error && 'has-error', 'mb-30px')}>
        {copy.emailPrompt}
      </BotResponse>

      <Input
        type="email"
        value={email}
        setValue={handleEmailChange}
        placeholder="Enter your BEST email address!"
        isInvalid={!!error}
        className="fade-in"
      />

      <p className="fade-in">{copy.consentNote}</p>

      {error === 'empty' && <p className="has-error">This field cannot be blank.</p>}
      {error === 'invalid' && <p className="has-error">Email is invalid.</p>}

      <div className="frm_submit">
        <Button label="Back" onClick={back} />
        <Button label={copy.submitLabel} className="auto-width" onClick={handleNext} />
      </div>
    </form>
  );
}
